import { returnToCreationTab } from './creation-tab-context';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';
import type { NavigationScreen } from 'app/types/navigation';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';

import type { Category } from 'app/types/category';
import { useAllCategories } from 'app/network/category';
import React, { useEffect } from 'react';
import type { CategoryIcons } from 'app/design/categories/tab';
import { CategoryTab } from 'app/design/categories/tab';
import { Platform, Pressable } from 'react-native';
import { useSetAtom } from 'jotai';
import { selectedCategoryAtom } from 'app/atoms';
import { usePageTitle } from 'app/utils/web-helpers';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { getI18nLanguage } from 'app/lib/language';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { navigateToListingWithParams } from 'app/screens/billboard/navigator-helper';
const theme = createDSTheme();

const CategoriesScreenComponent: NavigationScreen<'categories'> = ({ navigation, route }) => {
  const { t } = useTranslation('new');
  const i18nLanguage = getI18nLanguage();

  const setPageTitle = usePageTitle('categories');
  useEffect(() => setPageTitle('prompt.serviceRequest.title'), [setPageTitle]);

  const { data: categories } = useAllCategories();

  const setSelectedCategory = useSetAtom(selectedCategoryAtom);

  const openNewWithGuard = useOpenNewWithGuard();
  const onCategorySelected = (categoryId: Category['id']) => {
    // navigation.popTo()
    if (route.params.goBackTo === 'publish') {
      returnToCreationTab(navigation, { newCategoryId: categoryId });
    }
    if (route.params.goBackTo === 'new') {
      openNewWithGuard(navigation, {
        newCategoryId: categoryId,
      });
    }
    if (route.params.goBackTo === 'one-listing') {
      navigateToListingWithParams(navigation, {
        id: route.params.listingId!,
        newCategoryId: categoryId,
      });
    }
    if (route.params.goBackTo === 'edit-listing') {
      navigation.navigate(
        'edit-listing',
        {
          id: route.params.listingId!,
          newCategoryId: categoryId,
        },
        { pop: true },
      );
    }
    if (route.params.goBackTo === 'set-atom-then-close') {
      setSelectedCategory(categoryId === route.params.selectedCategory ? [] : [categoryId]);
      navigation.goBack();
    }
  };

  return (
    <Page isModal>
      <Column gap="spacing.general.x8">
        <Row>
          {Platform.OS === 'android' && (
            <Pressable onPress={() => navigation.goBack()}>
              <IconX strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
          )}
          <Text flex={1} fontWeight="600" textAlign="center" variant="headerXSmall">
            {t('prompt.serviceRequest.title')}
          </Text>
        </Row>

        <Column gap="spacing.general.x2">
          {categories?.map((cat) => (
            <RenderedButton
              icon={cat.icon as CategoryIcons}
              isSelected={route.params.selectedCategory === cat.id}
              key={cat.id}
              onPress={() => onCategorySelected(cat.id)}
              title={cat.name[i18nLanguage]}
            />
          ))}
        </Column>
      </Column>
    </Page>
  );
};

export const CategoriesScreen: NavigationScreen<'categories'> = withErrorBoundary(CategoriesScreenComponent);

const RenderedButton = ({
  icon,
  title,
  onPress,
  isSelected,
}: {
  icon: CategoryIcons;
  title: string;
  onPress: ButtonProps['onPress'];
  isSelected?: boolean;
}) => {
  return (
    <Button
      onPress={onPress}
      testID={`category-option-${icon}`}
      style={{
        height: 48,
        borderRadius: 24,
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x1'],
        backgroundColor: isSelected
          ? theme.colors['surface.inverse.tertiary-neutral']
          : theme.colors['surface.default'],
      }}
      variant="secondaryLight"
    >
      <Row alignItems="center" flex={1} gap="spacing.general.x2">
        <CategoryTab category={icon} size={24} />
        <Text fontWeight={isSelected ? '700' : '400'} numberOfLines={1} variant="bodyBase">
          {title}
        </Text>
      </Row>
    </Button>
  );
};
